import { FunctionComponent, useEffect, useState } from "react"
import styles from "./individualWellPage.module.scss"
import IwpSubNavigation from "../../components/individualWellPageComponents/subNavigation"
import { IWPChartData } from "../../components/individualWellPageComponents/wellDetailsChart"
import { ApolloError, useQuery } from "@apollo/client"
import { Outlet, useParams } from "react-router-dom"
import { getFragmentData } from "../../generated"
import { DATA_FRAME_FIELDS_FRAGMENT } from "../../graphql/fragments/dataFrame"
import {
    AlertMetricValuesInput,
    AlertRuleMetric,
    ControlIntentTimelineFieldsFragment,
    ControlObservationTimelineFieldsFragment,
    ControlSettingFieldsFragment,
    DataFrameFieldsFragment,
    DataPointFieldsFragment,
    EventType,
    GetControlSettingsForIntervalByWellIdQuery,
    GetNotificationsForUserQuery,
    GetWellNotesForIwpQuery,
    InstallationFieldsFragment,
    NotficationFragMyNotificationsFragment,
    WellAlertFieldsFragment,
    WellControlObservation,
    WellControlObservationStateFieldsFragment,
    WellIdentificationFieldsFragment,
    WellNotesForIwpFieldsFragment,
    WindowIdentifier,
    ZoneInfo,
} from "../../generated/graphql"
import {
    convertSecondsToHoursAndMinutes,
    getVariableDaysAgo_StartOfDayUnixMs,
} from "../../util/datesAndTimes/datesAndTimes"
import { WELL_IDENTIFICATION_FIELDS_FRAGMENT } from "../../graphql/fragments/wellIdentification"
import { DATA_POINT_FIELDS_FRAGMENT } from "../../graphql/fragments/dataPoint"
import { WELL_ALERT_FIELDS_FRAGMENT } from "../../graphql/fragments/wellAlert"
import {
    WELL_CONTROL_OBSERVATION_STATE_FIELDS_FRAGMENT,
    WELL_CONTROL_STATE_FIELDS_FRAGMENT,
} from "../../graphql/fragments/wellState"
import { CONTROL_SETTING_FIELDS_FRAGMENT } from "../../graphql/fragments/wellControlSetting"
import StatusSideBar from "../../components/individualWellPageComponents/statusSideBar"
import classNames from "classnames"
import { WellAttributesComponentI } from "../../components/individualWellPageComponents/wellAttributes"
import { INSTALLATION_FIELDS_FRAGMENT } from "../../graphql/fragments/installation"
import {
    CONTROL_INTENT_TIMELINE_FRAGMENT,
    CONTROL_OBSERVATION_TIMELINE_FRAGMENT,
    CONTROL_OBSERVATION_TIMELINE_SERIES_META_DATA_FRAGMENT,
} from "../../graphql/fragments/eventTimeline"
import { useViewerContext } from "../../context/ViewerContext"
import { NEW_INDIVIDUAL_WELL_PAGE_QUERY } from "../../graphql/queries/newIdividualWellPage"
import { MOTION_TIMELINE_QUERY } from "../../graphql/queries/motionTimeline"
import { GET_CONTROL_SETTINGS_FOR_INTERVAL_BY_WELL_ID } from "../../graphql/queries/controlSettingHistory"
import { GET_NOTIFICATIONS_FOR_USER } from "../../graphql/queries/notificationsForUser"
import { NOTIFICATION_FRAG_MY_NOTIFICATIONS } from "../../graphql/fragments/notifications"
import { UNEXPECTED_METRIC } from "../../graphql/queries/unexpectedWellTimes"
import dayjs from "dayjs"
import { getZoneInfoFromEnum } from "../../graphql/enum/zoneInfo"
import { WELL_NOTES_FOR_IWP } from "../../graphql/queries/wellNotes"
import { WELL_NOTES_FOR_IWP_FRAG } from "../../graphql/fragments/wellnote"
import { ReactSingleSelectOptionT } from "../../components/shared/input/react-select/singleSelect"

export interface IwpOutletContextI {
    wellDetails: IWPChartData
    wellAttributes: WellAttributesComponentI
    zoneInfo: ZoneInfo
    iwpLoading: boolean
    iwpMotionLoading: boolean
    iwpError?: ApolloError
    iwpMotionError?: ApolloError
    refetchWellMetrics?: (from: number, to: number) => void
}

// These variables are exported so they can be used as the variables in refetchQueries for unlinking & linking modals
// 395 days = 1 year 1
export const iwpQueryParam_equipStatsFrom =
    getVariableDaysAgo_StartOfDayUnixMs(395)
export const iwpQueryParam_equipStatsTo = Date.now()
export const iwpQueryParam_historyCount = 4

export const equipmentMetricsToMS = Date.now()
// 390 days ago
export const equipmentMetricsFromMS =
    equipmentMetricsToMS - 390 * 24 * 60 * 60 * 1000

// just today
const iwpMotionToMS = dayjs().valueOf()
const iwpMotionFromMS = dayjs().startOf("day").valueOf()

const IndividualWellPage: FunctionComponent = () => {
    const { wellId } = useParams()
    if (!wellId) throw new Error("wellId not resolved from url.")

    /** UI State variables */
    const initialBrowserWidth = window.innerWidth
    const [displayWellDetailsChart, setDisplayWellDetailsChart] = useState(true)
    const [displayStatusSideBar, setDisplayStatusSideBar] = useState(
        initialBrowserWidth > 992 ? true : false
    )
    const [windowSize, setWindowSize] = useState({
        width: initialBrowserWidth,
    })

    /** Contexts */
    const viewer = useViewerContext()
    const zoneInfo = viewer.getViewer().identity.zoneInfo

    /** Original query variables */
    const iwpQueryVariables = {
        wellInput: { id: wellId },
        historyCount: iwpQueryParam_historyCount,
        zoneInfo: viewer.getViewer().identity.zoneInfo,
        wellControlInput: { wellID: wellId },
        equipmentMetricsIntervalInput: {
            from: {
                unixMilliseconds: equipmentMetricsFromMS,
            },
            to: {
                unixMilliseconds: equipmentMetricsToMS,
            },
        },
        equipmentMetricsRollupDurationInput: {
            hours: 24,
        },
    }

    /** Motion query variables */
    const iwpMotionQueryVariables = {
        motionTimelineInput: {
            wellID: wellId,
            interval: {
                from: {
                    unixMilliseconds: iwpMotionFromMS,
                },
                to: {
                    unixMilliseconds: iwpMotionToMS,
                },
            },
            eventType: EventType.ControlObservation,
            motionOptions: {
                supplement: true,
                debounce: true,
            },
        },
        intentTimelineInput: {
            wellID: wellId,
            interval: {
                from: {
                    unixMilliseconds: iwpMotionFromMS,
                },
                to: {
                    unixMilliseconds: iwpMotionToMS,
                },
            },
            eventType: EventType.ControlIntent,
        },
    }

    const windowIdentifierVariables: WindowIdentifier = {
        interval: {
            from: {
                unixMilliseconds: dayjs(iwpMotionToMS)
                    .tz(getZoneInfoFromEnum(zoneInfo))
                    .startOf("day")
                    .valueOf(),
            },
            to: {
                unixMilliseconds: dayjs(iwpMotionToMS)
                    .tz(getZoneInfoFromEnum(zoneInfo))
                    .valueOf(),
            },
        },
    }

    const unexpectedDowntimeQueryVariables: AlertMetricValuesInput = {
        wellIDs: [wellId],
        queries: [
            {
                metric: AlertRuleMetric.UnexpectedDowntimeWindow,
                windowIdentifier: windowIdentifierVariables,
            },
        ],
    }

    const unexpectedRuntimeQueryVariables: AlertMetricValuesInput = {
        wellIDs: [wellId],
        queries: [
            {
                metric: AlertRuleMetric.UnexpectedRuntimeWindow,
                windowIdentifier: windowIdentifierVariables,
            },
        ],
    }

    /** Query individual well */
    const { error, data, loading } = useQuery(NEW_INDIVIDUAL_WELL_PAGE_QUERY, {
        variables: iwpQueryVariables,
        notifyOnNetworkStatusChange: true,
        // Because we are doing optimistic updates for well favorites - we cannot set the network policy to network-only.
        // Those two settings together will not broadcast the updates to the query
        // This is a bug in apollo client that they have acknowledged they don't have a good way to fix
        // See here: https://github.com/apollographql/apollo-client/issues/10802
        // Cache and network is the best option for now. We also have this issue with the well index query.
        fetchPolicy: "cache-and-network",
    })

    const iwpQueryError = error
    const iwpQueryData = data
    const iwpQueryLoading = loading

    const {
        error: motionError,
        data: motionData,
        loading: motionLoading,
        refetch,
    } = useQuery(MOTION_TIMELINE_QUERY, {
        variables: iwpMotionQueryVariables,
        notifyOnNetworkStatusChange: true,
        fetchPolicy: "cache-and-network",
    })

    const iwpMotionQueryError = motionError
    const iwpMotionQueryData = motionData
    const iwpMotionQueryLoading = motionLoading

    // query unexpected times
    const {
        error: unexpectedDowntimeError,
        data: unexpectedDowntimeData,
        loading: unexpectedDowntimeLoading,
        refetch: refetchUnexpectedDowntime,
    } = useQuery(UNEXPECTED_METRIC, {
        fetchPolicy: "cache-and-network",
        variables: { input: unexpectedDowntimeQueryVariables },
    })

    const {
        error: unexpectedRuntimeError,
        data: unexpectedRuntimeData,
        loading: unexpectedRuntimeLoading,
        refetch: refetchUnexpectedRuntime,
    } = useQuery(UNEXPECTED_METRIC, {
        variables: { input: unexpectedRuntimeQueryVariables },
        fetchPolicy: "cache-and-network",
    })

    const refetchMetrics = async (updatedFrom: number, updatedTo: number) => {
        try {
            await Promise.all([
                refetch({
                    motionTimelineInput: {
                        ...iwpMotionQueryVariables.motionTimelineInput,
                        interval: {
                            from: {
                                unixMilliseconds: updatedFrom,
                            },
                            to: {
                                unixMilliseconds: updatedTo,
                            },
                        },
                    },
                    intentTimelineInput: {
                        ...iwpMotionQueryVariables.intentTimelineInput,
                        interval: {
                            from: {
                                unixMilliseconds: updatedFrom,
                            },
                            to: {
                                unixMilliseconds: updatedTo,
                            },
                        },
                    },
                }),
                refetchUnexpectedDowntime({
                    input: {
                        wellIDs: [wellId],
                        queries: [
                            {
                                metric: AlertRuleMetric.UnexpectedDowntimeWindow,
                                windowIdentifier: {
                                    interval: {
                                        from: {
                                            unixMilliseconds: updatedFrom,
                                        },
                                        to: {
                                            unixMilliseconds: updatedTo,
                                        },
                                    },
                                },
                            },
                        ],
                    },
                }),
                refetchUnexpectedRuntime({
                    input: {
                        wellIDs: [wellId],
                        queries: [
                            {
                                metric: AlertRuleMetric.UnexpectedRuntimeWindow,
                                windowIdentifier: {
                                    interval: {
                                        from: {
                                            unixMilliseconds: updatedFrom,
                                        },
                                        to: {
                                            unixMilliseconds: updatedTo,
                                        },
                                    },
                                },
                            },
                        ],
                    },
                }),
            ])
        } catch (error) {
            console.log(error)
        }
    }

    let iwpNotificationsQueryError: ApolloError | undefined = undefined
    let iwpNotificationsQueryData: GetNotificationsForUserQuery | undefined =
        undefined
    let iwpNotificationsQueryLoading = false

    let wellNotesQueryError: ApolloError | undefined = undefined
    let wellNotesQueryData: GetWellNotesForIwpQuery | undefined = undefined
    let wellNotesQueryLoading = false

    let controlSettingsQueryError: ApolloError | undefined = undefined
    let controlSettingsQueryData:
        | GetControlSettingsForIntervalByWellIdQuery
        | undefined = undefined
    let controlSettingsQueryLoading = false

    const {
        error: notificationsError,
        data: notificationsData,
        loading: notificationsLoading,
    } = useQuery(GET_NOTIFICATIONS_FOR_USER, {
        variables: {
            NotificationsFilter: {
                resolvedHorizon: {
                    unixMilliseconds: equipmentMetricsFromMS,
                },
                filter: {
                    wellIDs: [wellId],
                },
            },
        },
        notifyOnNetworkStatusChange: true,
        fetchPolicy: "network-only",
    })

    iwpNotificationsQueryError = notificationsError
    iwpNotificationsQueryData = notificationsData
    iwpNotificationsQueryLoading = notificationsLoading

    const {
        error: wellNotesError,
        data: wellNotesData,
        loading: wellNotesLoading,
    } = useQuery(WELL_NOTES_FOR_IWP, {
        variables: { input: { wellIDs: [wellId] } },
        fetchPolicy: "network-only",
    })

    wellNotesQueryError = wellNotesError
    wellNotesQueryData = wellNotesData
    wellNotesQueryLoading = wellNotesLoading

    const {
        error: controlSettingsError,
        data: controlSettingsData,
        loading: controlSettingsLoading,
    } = useQuery(GET_CONTROL_SETTINGS_FOR_INTERVAL_BY_WELL_ID, {
        variables: {
            wellID: wellId,
            interval: {
                from: {
                    unixMilliseconds: equipmentMetricsFromMS,
                },
                to: {
                    unixMilliseconds: equipmentMetricsToMS,
                },
            },
        },
    })

    controlSettingsQueryError = controlSettingsError
    controlSettingsQueryData = controlSettingsData
    controlSettingsQueryLoading = controlSettingsLoading

    /** Parse Data fragments to pass to components */
    let runTimeDataFrame: DataFrameFieldsFragment | undefined = undefined
    let wellIdentificationfragment:
        | WellIdentificationFieldsFragment
        | undefined = undefined
    let runTimeDaily: DataFrameFieldsFragment | undefined = undefined
    let latestSpmDataPoint: DataPointFieldsFragment | undefined = undefined
    let wellAlertFragment: WellAlertFieldsFragment | undefined | null =
        undefined
    let observationFragment:
        | WellControlObservationStateFieldsFragment
        | undefined = undefined

    let controlSettingFragment: ControlSettingFieldsFragment | undefined =
        undefined
    let installationFragment: InstallationFieldsFragment | null | undefined
    let controlIntentTimelineFragments:
        | ControlIntentTimelineFieldsFragment[]
        | undefined

    let controlObservationTimelineFragments:
        | ControlObservationTimelineFieldsFragment[]
        | undefined

    const controlSettingsFragment: ControlSettingFieldsFragment[] = []
    const notificationsFragment: NotficationFragMyNotificationsFragment[] = []

    const wellNotes: WellNotesForIwpFieldsFragment[] = []
    let availableWells: ReactSingleSelectOptionT[] = []

    let runningAverage = "0m"
    let idleAverage = "0m"

    if (iwpQueryData && iwpQueryData.well && iwpQueryData.wells) {
        const { well } = iwpQueryData
        /** Parse Fragments to pass to children components */
        runTimeDataFrame = getFragmentData(
            DATA_FRAME_FIELDS_FRAGMENT,
            well.equipmentMetrics.windowedRunTime
        )

        wellIdentificationfragment = getFragmentData(
            WELL_IDENTIFICATION_FIELDS_FRAGMENT,
            well
        )
        runTimeDaily = getFragmentData(
            DATA_FRAME_FIELDS_FRAGMENT,
            well.equipmentMetrics.dailyRunTime
        )

        latestSpmDataPoint = getFragmentData(
            DATA_POINT_FIELDS_FRAGMENT,
            well.equipmentMetrics.latestSpm
        )
        wellAlertFragment = getFragmentData(
            WELL_ALERT_FIELDS_FRAGMENT,
            well.alert
        )
        const WellControlStateFieldsFragment = getFragmentData(
            WELL_CONTROL_STATE_FIELDS_FRAGMENT,
            well.state
        )
        observationFragment = getFragmentData(
            WELL_CONTROL_OBSERVATION_STATE_FIELDS_FRAGMENT,
            WellControlStateFieldsFragment.observation
        )
        controlSettingFragment = getFragmentData(
            CONTROL_SETTING_FIELDS_FRAGMENT,
            well.controlSetting
        )
        installationFragment = getFragmentData(
            INSTALLATION_FIELDS_FRAGMENT,
            well.installation
        )

        availableWells =
            iwpQueryData.wells?.nodes?.map((well) => {
                return {
                    value: well.id,
                    label: well.name,
                    disabled: false,
                }
            }) ?? []
    }

    if (iwpNotificationsQueryData) {
        iwpNotificationsQueryData.notificationsForUser.notifications.forEach(
            (notification) => {
                const got = getFragmentData(
                    NOTIFICATION_FRAG_MY_NOTIFICATIONS,
                    notification
                )
                notificationsFragment.push(got)
            }
        )
    }

    if (controlSettingsQueryData) {
        controlSettingsQueryData.wellControlSettingsByInterval.settings?.forEach(
            (cs) => {
                const got = getFragmentData(CONTROL_SETTING_FIELDS_FRAGMENT, cs)
                controlSettingsFragment.push(got)
            }
        )
    }

    if (iwpMotionQueryData) {
        iwpMotionQueryData.intentTimeline.series.timeline.forEach((event) => {
            if (event.__typename == "WellControlIntentTimelineEvent") {
                const got = getFragmentData(
                    CONTROL_INTENT_TIMELINE_FRAGMENT,
                    event
                )
                if (controlIntentTimelineFragments) {
                    controlIntentTimelineFragments.push(got)
                } else {
                    controlIntentTimelineFragments = [got]
                }
            }
        })

        if (
            iwpMotionQueryData.motionTimeline.series.__typename ==
            "WellControlObservationTimelineSeries"
        ) {
            const observationSeries = getFragmentData(
                CONTROL_OBSERVATION_TIMELINE_SERIES_META_DATA_FRAGMENT,
                iwpMotionQueryData.motionTimeline.series
            )

            observationSeries.timeline.forEach((event) => {
                if (event.__typename == "WellControlObservationTimelineEvent") {
                    const got = getFragmentData(
                        CONTROL_OBSERVATION_TIMELINE_FRAGMENT,
                        event
                    )
                    if (controlObservationTimelineFragments) {
                        controlObservationTimelineFragments.push(got)
                    } else {
                        controlObservationTimelineFragments = [got]
                    }
                }
            })

            observationSeries.metadata.stateDurations.forEach((state) => {
                if (state.state == WellControlObservation.Running) {
                    runningAverage = `${state.averageDuration.hours}h ${state.averageDuration.minutes}m`
                }
                if (state.state == WellControlObservation.Stopped) {
                    idleAverage = `${state.averageDuration.hours}h ${state.averageDuration.minutes}m`
                }
            })
        }

        if (wellNotesQueryData) {
            wellNotesQueryData.wellNotesByWellIDs.wellNotes.forEach((note) => {
                const got = getFragmentData(WELL_NOTES_FOR_IWP_FRAG, note)
                wellNotes.push(got)
            })
        }
    }

    let unexpectedDowntime = "0m"
    let unexpectedRuntime = "0m"

    if (unexpectedDowntimeData) {
        if (
            unexpectedDowntimeData.metricValues.results.length > 0 &&
            unexpectedDowntimeData.metricValues.results[0].values.length > 0
        ) {
            const converted = convertSecondsToHoursAndMinutes(
                unexpectedDowntimeData.metricValues.results[0].values[0].value
            )
            unexpectedDowntime = `${converted.hours}h ${converted.minutes}m`
        }
    }
    if (unexpectedRuntimeData) {
        if (
            unexpectedRuntimeData.metricValues.results.length > 0 &&
            unexpectedRuntimeData.metricValues.results[0].values.length > 0
        ) {
            const converted = convertSecondsToHoursAndMinutes(
                unexpectedRuntimeData.metricValues.results[0].values[0].value
            )
            unexpectedRuntime = `${converted.hours}h ${converted.minutes}m`
        }
    }

    /** Effect hooks */
    useEffect(() => {
        if (windowSize.width > 992) {
            setDisplayWellDetailsChart(true)
        }
        if (windowSize.width < 992 && displayWellDetailsChart) {
            setDisplayStatusSideBar(false)
        }
    }, [windowSize.width])

    useEffect(() => {
        const handleWindowWidthChange = () => {
            setWindowSize({
                width: window.innerWidth,
            })
        }

        window.addEventListener("resize", handleWindowWidthChange)

        return () => {
            window.removeEventListener("resize", handleWindowWidthChange)
        }
    }, [])

    /** Handlers */
    const handleToggleStatusSideBar = () => {
        if (windowSize.width > 992) {
            setDisplayStatusSideBar(!displayStatusSideBar)
            setDisplayWellDetailsChart(true)
        }
        if (windowSize.width < 992) {
            const currentSideBarStatus = displayStatusSideBar
            setDisplayStatusSideBar(!currentSideBarStatus)
            setDisplayWellDetailsChart(currentSideBarStatus)
        }
    }

    return (
        <>
            <div className={styles.container}>
                <IwpSubNavigation
                    toogleStatusBar={handleToggleStatusSideBar}
                    wellIdentificationfragment={wellIdentificationfragment}
                    windowWidth={windowSize.width}
                    availableWells={availableWells}
                    availableWellsLoading={iwpQueryLoading}
                />
                <div className={styles.bodyContainer}>
                    {displayStatusSideBar && (
                        <div className={styles.sidebarContainer}>
                            <StatusSideBar
                                loading={
                                    iwpQueryLoading ||
                                    iwpMotionQueryLoading ||
                                    iwpNotificationsQueryLoading ||
                                    unexpectedDowntimeLoading ||
                                    unexpectedRuntimeLoading ||
                                    wellNotesQueryLoading ||
                                    controlSettingsQueryLoading
                                }
                                error={
                                    iwpQueryError ||
                                    iwpMotionQueryError ||
                                    iwpNotificationsQueryError ||
                                    unexpectedDowntimeError ||
                                    unexpectedRuntimeError ||
                                    wellNotesQueryError ||
                                    controlSettingsQueryError
                                }
                                wellIdentificationFragment={
                                    wellIdentificationfragment
                                }
                                runTimeDaily={runTimeDaily}
                                latestSpmDataPoint={latestSpmDataPoint}
                                wellAlertFragment={wellAlertFragment}
                                observationFragment={observationFragment}
                                controlSetting={controlSettingFragment}
                            />
                        </div>
                    )}
                    {displayWellDetailsChart && (
                        <div
                            className={classNames(
                                styles.iwpOutletContainer,
                                displayStatusSideBar &&
                                    styles.iwpSideBarContainerActive
                            )}
                        >
                            <Outlet
                                key={wellId}
                                context={
                                    {
                                        wellDetails: {
                                            runTime: runTimeDataFrame,
                                            intentTimeline:
                                                controlIntentTimelineFragments,
                                            observationTimeline:
                                                controlObservationTimelineFragments,
                                            controlSettings:
                                                controlSettingsFragment,
                                            notification: notificationsFragment,
                                            unexpectedDowntime:
                                                unexpectedDowntime,
                                            unexpectedRuntime:
                                                unexpectedRuntime,
                                            runningAverage,
                                            idleAverage,
                                            wellNotes,
                                        },
                                        wellAttributes: {
                                            wellIdentificationFragment:
                                                wellIdentificationfragment,
                                            installationFragment:
                                                installationFragment,
                                            windowWidth: windowSize.width,
                                        },
                                        zoneInfo:
                                            viewer.getViewer().identity
                                                .zoneInfo,
                                        iwpLoading: iwpQueryLoading,
                                        iwpMotionLoading: iwpMotionQueryLoading,
                                        iwpError: iwpQueryError,
                                        iwpMotionError: iwpMotionQueryError,
                                        refetchWellMetrics: refetchMetrics,
                                    } satisfies IwpOutletContextI
                                }
                            />
                        </div>
                    )}
                </div>
            </div>
        </>
    )
}

export default IndividualWellPage
